import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box } from '@mui/material';

type ChevronRedCircleProps = {
  centered?: boolean;
  className?: string;
  direction?: 'left' | 'right';
  disabled?: boolean;
  scale?: number;
  styles?: object;
};

/**
 * ChevronRedCircle
 *
 * Renders the chevron red circle
 * @param className - the class name
 * @param direction - the direction of the chevron
 * @param disabled - whether the chevron is disabled
 * @param scale - the scale of the chevron
 * @param styles - the styles of the chevron
 */
export function ChevronRedCircle({
  centered = false,
  className,
  direction = 'right',
  disabled = false,
  scale = 1,
  styles = {},
}: ChevronRedCircleProps): JSX.Element {
  const Icon = direction === 'left' ? ChevronLeftIcon : ChevronRightIcon;
  const baseSize = 12;
  const size = centered ? baseSize * scale : baseSize;
  const iconSize = centered ? size * 0.55 : baseSize;

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        ...styles,
      }}
    >
      <Box
        data-test-id="chevron-selector"
        className={className}
        sx={(theme) => ({
          alignItems: 'center',
          background: !disabled
            ? theme.colors.primary
            : theme.palette?.common?.white,
          borderRadius: '100%',
          color: !disabled
            ? theme.palette?.common?.white
            : theme.palette?.grey?.[400],
          display: 'flex',
          flexShrink: 0,
          height: centered ? size : baseSize,
          justifyContent: 'center',
          lineHeight: `${centered ? size : baseSize}px`,
          marginRight: centered || direction !== 'right' ? undefined : '4px',
          transform: centered ? undefined : `scale(${scale})`,
          transition: 'none',
          width: centered ? size : baseSize,
        })}
      >
        <Icon
          sx={{
            height: centered ? `${iconSize}px` : '12px',
            width: centered ? `${iconSize}px` : '12px',
          }}
        />
      </Box>
    </Box>
  );
}

/**
 * ChevronRightRedCircle
 *
 * Renders the right chevron red circle
 * @param props - the props for the chevron red circle
 */
export function ChevronRightRedCircle(
  props: Omit<ChevronRedCircleProps, 'direction'>
): JSX.Element {
  return <ChevronRedCircle direction="right" {...props} />;
}
